/**
 * Transfer protocol sender.
 *
 * Guarantees (the "hard part" of the product):
 * - **Backpressure**: before enqueuing a chunk it waits for `bufferedAmount`
 *   to drop below the threshold (`onBufferedLow` event); it never floods the channel or RAM.
 * - **Streaming**: it reads the file in windows from the `FileSource` (never whole).
 * - **Resumption**: it starts each file at the `offset` requested by the receiver
 *   in the `accept` (bytes it already had from a previous session).
 * - **Integrity**: CRC32 per chunk (in the frame) + SHA-256 of the complete file
 *   in `fileEnd`, which the receiver verifies and confirms with `fileAck`.
 * - **Bounded retry**: if the receiver responds `fileAck{ok:false}` (SHA does not
 *   match), the file is RESENT whole (offset 0; the receiver's
 *   writes are positional and idempotent) up to `maxFileRetries` times.
 *   Once the retries are exhausted, `abort` is sent and the channel is CLOSED — the
 *   receiver never stays hung waiting.
 * - **Progress**: callback per chunk with bytes per file and totals.
 */

import { dataChannelAdapter, type TransferChannel } from "./channel";
import { encodeChunk } from "./framing";
import { createSha256 } from "./hash";
import { decodeControl, encodeControl, type FileMeta } from "./protocol";
import type { FileSource } from "./source";

export interface TransferProgress {
  fileIndex: number;
  fileName: string;
  fileSentBytes: number;
  fileTotalBytes: number;
  totalSentBytes: number;
  totalBytes: number;
}

export interface SendOptions {
  /** Chunk size (bytes). Default 16 KiB (safe for SCTP/DataChannel). */
  chunkSize?: number;
  /** High water mark: if `bufferedAmount` exceeds it, pumping is paused. */
  highWaterMark?: number;
  /** Full resends of a file on `fileAck{ok:false}`. Default 2. */
  maxFileRetries?: number;
  onProgress?: (progress: TransferProgress) => void;
  /**
   * The receiver accepted: `resume[i]` = bytes it ALREADY has of file i (>0 when
   * resuming a cut transfer). Useful for UX ("resuming from…").
   */
  onAccept?: (resume: readonly number[]) => void;
  /** A file failed verification and is going to be resent (attempt `attempt`). */
  onFileRetry?: (fileIndex: number, attempt: number) => void;
  onFileDone?: (fileIndex: number) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
  onReject?: (reason: string) => void;
}

const DEFAULT_CHUNK = 16 * 1024;
const DEFAULT_HIGH_WATER = 1024 * 1024;
const DEFAULT_FILE_RETRIES = 2;

/** Starts the transfer of `sources` over `channel`. Returns a handle. */
export function sendFiles(
  channel: TransferChannel,
  sources: readonly FileSource[],
  options: SendOptions = {},
): { cancel: () => void } {
  const chunkSize = options.chunkSize ?? DEFAULT_CHUNK;
  const highWater = options.highWaterMark ?? DEFAULT_HIGH_WATER;
  const totalBytes = sources.reduce((sum, s) => sum + s.size, 0);

  let cancelled = false;
  let closed = false;
  const drainWaiters: (() => void)[] = [];
  let acceptResume: number[] | undefined;
  let acceptResolve: (() => void) | undefined;
  let acceptReject: ((reason: string) => void) | undefined;
  const ackWaiters = new Map<number, (ok: boolean) => void>();

  channel.onBufferedLow(() => {
    const waiters = drainWaiters.splice(0);
    for (const w of waiters) {
      w();
    }
  });

  channel.onClose(() => {
    closed = true;
    const waiters = drainWaiters.splice(0);
    for (const w of waiters) {
      w();
    }
    // It also releases whoever waits for a fileAck: with the channel down it will not arrive.
    const acks = [...ackWaiters.values()];
    ackWaiters.clear();
    for (const resolveAck of acks) {
      resolveAck(false);
    }
  });

  channel.onMessage((data) => {
    if (typeof data !== "string") {
      return; // el emisor no espera binario
    }
    const message = decodeControl(data);
    if (!message) {
      return;
    }
    if (message.t === "accept") {
      acceptResume = message.resume;
      acceptResolve?.();
    } else if (message.t === "reject") {
      acceptReject?.(message.reason);
    } else if (message.t === "fileAck") {
      ackWaiters.get(message.i)?.(message.ok);
    }
  });

  function waitForDrain(): Promise<void> {
    return new Promise((resolve) => {
      drainWaiters.push(resolve);
    });
  }

  function waitForAck(index: number): Promise<boolean> {
    return new Promise((resolve) => {
      ackWaiters.set(index, resolve);
    });
  }

  /**
   * Pumps file `index` once from `startOffset` and waits for the `fileAck`.
   * Returns the receiver's integrity verdict.
   */
  async function pumpOnce(
    index: number,
    source: FileSource,
    startOffset: number,
  ): Promise<boolean> {
    const sha = createSha256();
    let fileSent = startOffset;
    let priorTotal = 0;
    for (let k = 0; k < index; k += 1) {
      priorTotal += sources[k]?.size ?? 0;
    }
    channel.send(encodeControl({ t: "fileStart", v: 1, i: index, offset: startOffset }));

    for (let pos = 0; pos < source.size; pos += chunkSize) {
      if (cancelled || closed) {
        throw new Error("channel-closed");
      }
      const end = Math.min(pos + chunkSize, source.size);
      const window = await source.slice(pos, end);
      sha.update(window); // the hash covers the ENTIRE file (includes resumed prefix)
      if (end <= startOffset) {
        continue; // receiver already has the whole chunk: hash it, do not resend
      }
      // Backpressure: do not enqueue if the buffer is full. The loop condition
      // is a call (not a variable), and `closed`/`cancelled` (mutated from
      // callbacks) are checked in the body to be able to abort the pumping.
      while (channel.bufferedAmount() > highWater) {
        if (closed || cancelled) {
          throw new Error("channel-closed");
        }
        await waitForDrain();
      }
      if (cancelled || closed) {
        throw new Error("channel-closed");
      }
      channel.send(encodeChunk(index, pos, window));
      fileSent = pos + window.length;
      options.onProgress?.({
        fileIndex: index,
        fileName: source.name,
        fileSentBytes: fileSent,
        fileTotalBytes: source.size,
        totalSentBytes: priorTotal + fileSent,
        totalBytes,
      });
    }

    const digest = await sha.digestHex();
    channel.send(encodeControl({ t: "fileEnd", v: 1, i: index, sha256: digest }));
    const ok = await waitForAck(index);
    ackWaiters.delete(index);
    return ok;
  }

  /** Pumps the file with bounded retry on integrity failure. */
  async function pumpFile(index: number, source: FileSource, startOffset: number): Promise<void> {
    const maxRetries = options.maxFileRetries ?? DEFAULT_FILE_RETRIES;
    let offset = startOffset;
    for (let attempt = 0; ; attempt += 1) {
      const ok = await pumpOnce(index, source, offset);
      if (ok) {
        options.onFileDone?.(index);
        return;
      }
      if (cancelled || closed) {
        throw new Error("channel-closed");
      }
      if (attempt >= maxRetries) {
        throw new Error(`integrity-failed:${index}`);
      }
      // FULL resend: the SHA failed and it is not known which chunk was lost/corrupted;
      // the receiver's writes are positional, so rewriting everything
      // from 0 leaves the file consistent.
      offset = 0;
      options.onFileRetry?.(index, attempt + 1);
    }
  }

  async function run(): Promise<void> {
    const files: FileMeta[] = sources.map((s) => ({ name: s.name, size: s.size, mime: s.mime }));
    const accepted = new Promise<void>((resolve, reject) => {
      acceptResolve = resolve;
      acceptReject = (reason) => {
        reject(new Error(`rejected:${reason}`));
      };
    });
    channel.send(encodeControl({ t: "offer", v: 1, files }));
    await accepted;
    const resume = acceptResume ?? [];
    options.onAccept?.(resume);

    for (let i = 0; i < sources.length; i += 1) {
      const src = sources[i];
      if (!src) {
        continue;
      }
      const startOffset = Math.min(resume[i] ?? 0, src.size);
      await pumpFile(i, src, startOffset);
    }
    channel.send(encodeControl({ t: "bye", v: 1 }));
    options.onDone?.();
  }

  run().catch((error: unknown) => {
    if (cancelled) {
      return;
    }
    const err = error instanceof Error ? error : new Error("send-failed");
    // Surrender notice + close: the receiver must not stay waiting for a
    // resend that will never arrive. `send` over an already down channel may throw.
    if (!closed) {
      try {
        channel.send(encodeControl({ t: "abort", v: 1, reason: err.message }));
      } catch {
        /* channel down: the close already propagated on its own */
      }
    }
    channel.close();
    if (err.message.startsWith("rejected:")) {
      options.onReject?.(err.message.slice("rejected:".length));
    } else {
      options.onError?.(err);
    }
  });

  return {
    cancel() {
      cancelled = true;
      channel.close();
    },
  };
}

/** Sends a text message (clipboard) over the channel, without files. */
export function sendText(channel: TransferChannel, body: string): void {
  channel.send(encodeControl({ t: "text", v: 1, body }));
}

/** Sugar: creates the adapter of an `RTCDataChannel` and starts the send. */
export function sendFilesOverDataChannel(
  dc: RTCDataChannel,
  sources: readonly FileSource[],
  options?: SendOptions,
): { cancel: () => void } {
  return sendFiles(dataChannelAdapter(dc), sources, options);
}
